import { z } from 'zod/v4';
import { INormalText, NORMAL_TEXT_SCHEMA } from '../text/text.interfaces';
import { ETicketType } from './ticket.enums';
import { IDirectTicket } from '../direct-ticket/direct-ticket.interfaces';
import { ISubscriptionTicket } from '../subscription.interfaces';
import { IAccessibleImage } from '../image.interfaces';
import { Brand } from '../../branded-types';

export type TicketId = Brand<string, 'ticketId'>;
export type TicketDetailId = Brand<string, 'ticketDetailId'>;
export type TicketCategoryId = Brand<string, 'ticketCategoryId'>;

export const TICKET_DETAILS: z.ZodType<ITicketDetail> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as TicketDetailId)
    .optional(),
  icon: z.string().optional(),
  link: z.string(),
  type: z.literal(['link', 'text']),
  sortPosition: z.number(),
  texts: z.array(NORMAL_TEXT_SCHEMA),
});

export const TICKET_SCHEMA: z.ZodType<ITicket> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as TicketId)
    .optional(),
  ticketId: z.string(),
  serviceAreaId: z.number().int().optional(),
  isActive: z.boolean(),
  displayPrice: z.boolean().optional(),
  startDate: z.coerce.date().optional().nullable(),
  endDate: z.coerce.date().optional().nullable(),
  texts: z.array(NORMAL_TEXT_SCHEMA).optional(),
  details: z.array(TICKET_DETAILS).optional(),
  ticketCategoryId: z
    .string()
    .transform((s: string) => s as TicketCategoryId)
    .optional()
    .nullable(),
  positionInCategory: z.number().optional().nullable(),
});

export const TICKET_CATEGORY_SCHEMA: z.ZodType<ITicketCategory> = z.object({
  id: z
    .uuid()
    .transform((s: string) => s as TicketCategoryId)
    .optional(),
  name: z.string(),
  position: z.number(),
  ticketType: z.enum(ETicketType),
});

export const TICKET_CATEGORIES_SCHEMA: z.ZodType<ITicketCategory[]> = z.array(
  TICKET_CATEGORY_SCHEMA
);

export const TICKETS_SCHEMA: z.ZodType<ITicket[]> = z.array(TICKET_SCHEMA);

export interface ITicket {
  id?: TicketId;
  ticketId: string;
  serviceAreaId?: number;
  isActive: boolean;
  displayPrice?: boolean;
  startDate?: Date | null;
  endDate?: Date | null;
  accessibleImage?: IAccessibleImage;
  texts?: INormalText[];
  details?: ITicketDetail[];
  ticketCategoryId?: TicketCategoryId | null;
  positionInCategory?: number | null;
}

export const deriveTicketIsActive = (
  ticketCategoryId: TicketCategoryId | null | undefined
): boolean => ticketCategoryId != null;

export interface IWithTicketData {
  type: ETicketType;
  ticketData?: ITicket;
}

export type ShopTicket = IDirectTicket | ISubscriptionTicket;

export interface ITicketDetail {
  id?: TicketDetailId;
  icon?: string;
  type: 'link' | 'text';
  link: string;
  sortPosition: number;
  texts: INormalText[];
}

export const DEFAULT_TICKET: ITicket = {
  ticketId: '',
  isActive: false,
  displayPrice: true,
  texts: [],
  details: [],
  ticketCategoryId: null,
  positionInCategory: null,
};

export const DEFAULT_TICKET_CATEGORY: ITicketCategory = {
  id: '3e937885-90e7-42eb-b9bd-d5eds1f8e229' as TicketCategoryId,
  name: '',
  position: 1,
  ticketType: ETicketType.Direct,
};

export interface ITicketCategory {
  id?: TicketCategoryId;
  name: string;
  position: number;
  ticketType: ETicketType;
}

export const DEFAULT_TICKET_CATEGORIES: ITicketCategory[] = [
  DEFAULT_TICKET_CATEGORY,
];

export const UNASSIGNED_TICKET_LIST = 'unassigned-ticket-list';

export enum ETicketCategorySubMenuAction {
  switchToRight = 0,
  switchToLeft = 1,
  rename = 2,
  delete = 3,
}

export interface ISubMenuItem {
  text: string;
  action: ETicketCategorySubMenuAction;
}
